import React, { useState, useEffect, useCallback } from 'react';
import {
  customerAdsAdminService,
  CustomerAd,
} from '../services/customerAdsAdmin';
import './CustomerAdsPage.css';

export const CustomerAdsPage: React.FC = () => {
  const [ads, setAds] = useState<CustomerAd[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<'pending' | 'approved' | 'rejected'>('pending');
  const [busyId, setBusyId] = useState<string | null>(null);

  const loadAds = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await customerAdsAdminService.listCustomerAds({
        limit: 100,
        status: statusFilter,
      });
      setAds(res.ads);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load customer ads');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    loadAds();
  }, [loadAds]);

  const approve = async (adId: string) => {
    setBusyId(adId);
    try {
      await customerAdsAdminService.approveCustomerAd(adId);
      await loadAds();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Approve failed');
    } finally {
      setBusyId(null);
    }
  };

  const reject = async (adId: string) => {
    const reason = window.prompt('Optional rejection reason (shown to customer):') ?? undefined;
    setBusyId(adId);
    try {
      await customerAdsAdminService.rejectCustomerAd(adId, reason || undefined);
      await loadAds();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Reject failed');
    } finally {
      setBusyId(null);
    }
  };

  const removeAd = async (adId: string) => {
    if (!window.confirm('Delete this ad for everyone? DynamoDB row and S3 image will be removed.')) {
      return;
    }
    setBusyId(adId);
    try {
      await customerAdsAdminService.deleteCustomerAd(adId);
      await loadAds();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Delete failed');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="customer-ads-page">
      <header className="customer-ads-header">
        <h1>Customer ads</h1>
        <p className="customer-ads-subtitle">
          Approve for the public Community ads feed. Anyone can browse approved ads in the app. Delete removes the ad for everyone.
        </p>
      </header>

      <div className="customer-ads-filters">
        {(['pending', 'approved', 'rejected'] as const).map((s) => (
          <button
            key={s}
            type="button"
            className={`filter-btn ${statusFilter === s ? 'active' : ''}`}
            onClick={() => setStatusFilter(s)}
          >
            {s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
        <button type="button" className="filter-btn secondary" onClick={() => loadAds()}>
          Refresh
        </button>
      </div>

      {error && <div className="customer-ads-error">{error}</div>}

      {loading ? (
        <p>Loading…</p>
      ) : ads.length === 0 ? (
        <p className="muted">No ads in this queue.</p>
      ) : (
        <div className="customer-ads-grid">
          {ads.map((ad) => (
            <article key={ad.adId} className="customer-ads-card">
              <div className="customer-ads-thumb-wrap">
                <img src={ad.imageViewUrl || ad.imageUrl} alt="" className="customer-ads-thumb" />
              </div>
              <div className="customer-ads-meta">
                <div>
                  <strong>User</strong> <span className="mono">{ad.userId.slice(0, 12)}…</span>
                </div>
                <div>
                  <strong>Status</strong> {ad.status}
                </div>
                <div>
                  <strong>Submitted</strong> {new Date(ad.createdAt).toLocaleString()}
                </div>
                {ad.caption ? (
                  <div>
                    <strong>Caption</strong> {ad.caption}
                  </div>
                ) : null}
                {ad.rejectionReason ? (
                  <div className="reject-reason">
                    <strong>Rejection</strong> {ad.rejectionReason}
                  </div>
                ) : null}
              </div>
              {ad.status === 'pending' && (
                <div className="customer-ads-actions">
                  <button
                    type="button"
                    className="btn-approve"
                    disabled={busyId === ad.adId}
                    onClick={() => approve(ad.adId)}
                  >
                    {busyId === ad.adId ? '…' : 'Approve'}
                  </button>
                  <button
                    type="button"
                    className="btn-reject"
                    disabled={busyId === ad.adId}
                    onClick={() => reject(ad.adId)}
                  >
                    Reject
                  </button>
                </div>
              )}
              <div className="customer-ads-actions customer-ads-actions-delete">
                <button
                  type="button"
                  className="btn-delete-admin"
                  disabled={busyId === ad.adId}
                  onClick={() => removeAd(ad.adId)}
                >
                  {busyId === ad.adId ? '…' : 'Delete permanently'}
                </button>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
};
